import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import adminApi from "../../services/adminApi";
import AdminTable from "./AdminTable";
import LoadingState from "./LoadingState";
import toast from "react-hot-toast";
import "../../styles/Admin.css";

const UserServicesView = ({ userId }) => {
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("droplets");
  const [suspendingDropletId, setSuspendingDropletId] = useState(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [selectedDroplet, setSelectedDroplet] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [updatingSpaceId, setUpdatingSpaceId] = useState(null);
  const [showSpaceSuspendModal, setShowSpaceSuspendModal] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [spaceSuspendReason, setSpaceSuspendReason] = useState("");

  useEffect(() => {
    if (userId) {
      loadServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadServices = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/users/${userId}/services`);
      setServices({
        ...data,
        spaces: data.spaces || [],
      });
    } catch (error) {
      console.error("Failed to load user services:", error);
      setServices({
        droplets: [],
        snapshots: [],
        backups: [],
        firewalls: [],
        spaces: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendClick = (droplet) => {
    setSelectedDroplet(droplet);
    setSuspendReason("");
    setShowSuspendModal(true);
  };

  const handleSuspendConfirm = async () => {
    if (!selectedDroplet) return;

    setShowSuspendModal(false);
    setSuspendingDropletId(selectedDroplet.id);
    try {
      await adminApi.post(`/admin/droplets/${selectedDroplet.id}/suspend`, {
        reason: suspendReason || "Admin suspension",
      });
      toast.success("Droplet suspended successfully");
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to suspend droplet");
    } finally {
      setSuspendingDropletId(null);
      setSelectedDroplet(null);
      setSuspendReason("");
    }
  };

  const handleUnsuspendDroplet = async (dropletId) => {
    setSuspendingDropletId(dropletId);
    try {
      await adminApi.post(`/admin/droplets/${dropletId}/unsuspend`);
      toast.success("Droplet unsuspended successfully");
      loadServices();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to unsuspend droplet"
      );
    } finally {
      setSuspendingDropletId(null);
    }
  };

  const handleSpaceSuspendClick = (space) => {
    setSelectedSpace(space);
    setSpaceSuspendReason("");
    setShowSpaceSuspendModal(true);
  };

  const handleSpaceSuspendConfirm = async () => {
    if (!selectedSpace) return;
    setShowSpaceSuspendModal(false);
    setUpdatingSpaceId(selectedSpace.id);
    try {
      await adminApi.post(
        `/admin/users/${userId}/spaces/${selectedSpace.id}/suspend`,
        {
          reason: spaceSuspendReason || "Suspended by admin",
        }
      );
      toast.success("Space suspended successfully");
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to suspend Space");
    } finally {
      setUpdatingSpaceId(null);
      setSelectedSpace(null);
      setSpaceSuspendReason("");
    }
  };

  const handleSpaceUnsuspend = async (spaceId) => {
    setUpdatingSpaceId(spaceId);
    try {
      await adminApi.post(`/admin/users/${userId}/spaces/${spaceId}/unsuspend`);
      toast.success("Space unsuspended successfully");
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unsuspend Space");
    } finally {
      setUpdatingSpaceId(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading user services..." />;
  }

  const dropletsColumns = [
    { key: "name", label: "Name" },
    { key: "region", label: "Region" },
    { key: "size", label: "Size" },
    { key: "status", label: "Status" },
    {
      key: "ip_address",
      label: "IP Address",
      render: (value) => value || "-",
    },
    {
      key: "suspension",
      label: "Suspension",
      render: (_, droplet) => {
        if (!droplet.suspended_at)
          return <span className="admin-badge admin-badge-active">Active</span>;
        return (
          <span
            className={`admin-badge ${
              droplet.suspended_by_admin
                ? "admin-badge-suspended"
                : "admin-badge-warning"
            }`}
          >
            {droplet.suspended_by_admin ? "Admin Suspended" : "Overdue"}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, droplet) => {
        if (!droplet.suspended_at) {
          return (
            <button
              className="btn-danger btn-small"
              onClick={() => handleSuspendClick(droplet)}
              disabled={suspendingDropletId === droplet.id}
              title="Suspend droplet"
            >
              {suspendingDropletId === droplet.id ? "..." : "Suspend"}
            </button>
          );
        }
        return (
          <button
            className="btn-primary btn-small"
            onClick={() => handleUnsuspendDroplet(droplet.id)}
            disabled={suspendingDropletId === droplet.id}
            title="Unsuspend droplet"
          >
            {suspendingDropletId === droplet.id ? "..." : "Unsuspend"}
          </button>
        );
      },
    },
  ];

  const snapshotsColumns = [
    { key: "name", label: "Name" },
    { key: "snapshot_id", label: "Snapshot ID" },
    {
      key: "size_gb",
      label: "Size (GB)",
      render: (value) => (value ? `${value} GB` : "-"),
    },
    {
      key: "created_at",
      label: "Created",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
  ];

  const backupsColumns = [
    { key: "name", label: "Name" },
    { key: "backup_id", label: "Backup ID" },
    {
      key: "size_gb",
      label: "Size (GB)",
      render: (value) => (value ? `${value} GB` : "-"),
    },
    {
      key: "created_at",
      label: "Created",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
  ];

  const firewallsColumns = [
    { key: "name", label: "Name" },
    { key: "firewall_id", label: "Firewall ID" },
    {
      key: "created_at",
      label: "Created",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
  ];

  const spacesColumns = [
    { key: "name", label: "Name" },
    {
      key: "region",
      label: "Region",
      render: (_, space) => {
        const identifier = space.resource_identifier || "";
        const [, region] = identifier.split(":");
        const config =
          typeof space.config === "string"
            ? JSON.parse(space.config)
            : space.config || {};
        return region || config.region || "-";
      },
    },
    {
      key: "status",
      label: "Status",
      render: (value) => value || "-",
    },
    {
      key: "suspension",
      label: "Suspension",
      render: (_, space) => {
        if (!space.suspended_at) {
          return <span className="admin-badge admin-badge-active">Active</span>;
        }
        if (space.suspended_by_admin) {
          return (
            <span className="admin-badge admin-badge-suspended">
              Admin Suspended
            </span>
          );
        }
        return <span className="admin-badge admin-badge-warning">Overdue</span>;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, space) => {
        if (space.suspended_at) {
          return (
            <button
              className="btn-primary btn-small"
              onClick={() => handleSpaceUnsuspend(space.id)}
              disabled={updatingSpaceId === space.id}
            >
              {updatingSpaceId === space.id ? "..." : "Unsuspend"}
            </button>
          );
        }
        return (
          <button
            className="btn-danger btn-small"
            onClick={() => handleSpaceSuspendClick(space)}
            disabled={updatingSpaceId === space.id}
          >
            {updatingSpaceId === space.id ? "..." : "Suspend"}
          </button>
        );
      },
    },
  ];

  const tabs = [
    {
      id: "droplets",
      label: "Droplets",
      count: services?.droplets?.length || 0,
    },
    {
      id: "snapshots",
      label: "Snapshots",
      count: services?.snapshots?.length || 0,
    },
    { id: "backups", label: "Backups", count: services?.backups?.length || 0 },
    {
      id: "firewalls",
      label: "Firewalls",
      count: services?.firewalls?.length || 0,
    },
    {
      id: "spaces",
      label: "Spaces",
      count: services?.spaces?.length || 0,
    },
  ];

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">User Services</h2>
      </div>

      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {activeTab === "droplets" && (
        <div className="admin-tab-content active">
          <AdminTable
            columns={dropletsColumns}
            data={services?.droplets || []}
            loading={false}
            emptyMessage="No droplets found"
          />
        </div>
      )}

      {activeTab === "snapshots" && (
        <div className="admin-tab-content active">
          <AdminTable
            columns={snapshotsColumns}
            data={services?.snapshots || []}
            loading={false}
            emptyMessage="No snapshots found"
          />
        </div>
      )}

      {activeTab === "backups" && (
        <div className="admin-tab-content active">
          <AdminTable
            columns={backupsColumns}
            data={services?.backups || []}
            loading={false}
            emptyMessage="No backups found"
          />
        </div>
      )}

      {activeTab === "firewalls" && (
        <div className="admin-tab-content active">
          <AdminTable
            columns={firewallsColumns}
            data={services?.firewalls || []}
            loading={false}
            emptyMessage="No firewalls found"
          />
        </div>
      )}

      {activeTab === "spaces" && (
        <div className="admin-tab-content active">
          <AdminTable
            columns={spacesColumns}
            data={services?.spaces || []}
            loading={false}
            emptyMessage="No spaces found"
          />
        </div>
      )}

      {/* Suspend Droplet Modal */}
      <Modal
        show={showSuspendModal}
        onHide={() => setShowSuspendModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Suspend Droplet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Suspension Reason <span className="text-muted">(optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason for suspension (e.g., Abuse report, Payment issue, etc.)"
            />
            {selectedDroplet && (
              <div className="text-muted admin-form-helper">
                Droplet: <strong>{selectedDroplet.name}</strong>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              setShowSuspendModal(false);
              setSuspendReason("");
              setSelectedDroplet(null);
            }}
          >
            Cancel
          </button>
          <button
            className="btn-danger btn-sm"
            onClick={handleSuspendConfirm}
            disabled={suspendingDropletId === selectedDroplet?.id}
          >
            {suspendingDropletId === selectedDroplet?.id
              ? "Suspending..."
              : "Suspend"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSpaceSuspendModal}
        onHide={() => setShowSpaceSuspendModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Suspend Space</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Suspension Reason <span className="text-muted">(optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={spaceSuspendReason}
              onChange={(e) => setSpaceSuspendReason(e.target.value)}
              placeholder="Enter reason for suspension (e.g., abuse report, payment issue, etc.)"
            />
            {selectedSpace && (
              <div className="text-muted admin-form-helper">
                Space: <strong>{selectedSpace.name}</strong>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              setShowSpaceSuspendModal(false);
              setSpaceSuspendReason("");
              setSelectedSpace(null);
            }}
          >
            Cancel
          </button>
          <button
            className="btn-danger btn-sm"
            onClick={handleSpaceSuspendConfirm}
            disabled={updatingSpaceId === selectedSpace?.id}
          >
            {updatingSpaceId === selectedSpace?.id
              ? "Suspending..."
              : "Suspend"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserServicesView;
